import _get from 'lodash/get'
import _omit from 'lodash/omit'

import { personal_details } from '@flows/configs/shared/steps/personal_details'
import type { StepConfigParams } from '@flows/configs/types'
import type { AnyStepConfiguration, AnySteps, FlowValues } from '@flows/core/types/stepTypes'

import breed_type from './breed_type'
import condition_area from './condition_area'
import condition_illness from './condition_illness'
import cross_breed_names from './cross_breed_names'
import current_health from './current_health'
import pet_birthday from './pet_birthday'
import pet_details from './pet_details'
import pet_gender from './pet_gender'
import pet_health_v2_consent from './pet_health_v2_consent'
import pet_health_v2_purchase from './pet_health_v2_purchase'
import pet_name from './pet_name'
import pet_quote from './pet_quote'
import pet_type from './pet_type'
import postal_code from './postal_code'
import pre_existing_conditions from './pre_existing_conditions'
import pure_breed_name from './pure_breed_name'
import shoulder_height from './shoulder_height'

const getSharedSteps = (params: StepConfigParams, platform: 'native' | 'web') =>
  ({
    shoulder_height: shoulder_height(params, platform),
    pet_quote: pet_quote(params),
    pet_health_v2_personal_details: personal_details(params, {
      nextStep: 'pre_existing_conditions',
      lockBirthdate: true,
      translations: {
        title: 'sharedFlows_common:steps.personal_details.title',
        birthdateLabel: 'sharedFlows_common:steps.personal_details.birthdate.input.label',
        marketingOptIn: 'sharedFlows_common:steps.email_and_name.checkboxes.marketingOptIn',
        minAgeError: 'sharedFlows_common:generic.errors.min_age',
        maxAgeError: 'sharedFlows_common:generic.errors.max_age',
      },
      trackContactBlur: true,
    }),
    pre_existing_conditions: pre_existing_conditions(params, platform),
    condition_area: condition_area(params),
    condition_illness: condition_illness(params),
    current_health: current_health(params, platform),
  }) as const satisfies AnySteps

const hasProfileDetails = (flowValues: FlowValues) =>
  String(_get(flowValues, 'helperValues.detailsSet') === true)

const nativeQuote = (params: StepConfigParams) =>
  ({
    ..._omit(pet_quote(params), 'nextStep'),
    nextStepConfig: {
      valueFunction: hasProfileDetails,
      valueToStepName: { true: 'pre_existing_conditions', false: 'pet_health_v2_personal_details' },
    },
  }) as const satisfies AnyStepConfiguration

export const getNativeSteps = (params: StepConfigParams) =>
  ({
    pet_name: pet_name(params),
    pet_type: pet_type(params),
    pet_birthday: pet_birthday(params),
    pet_gender: pet_gender(params),
    breed_type: breed_type(params),
    pure_breed_name: pure_breed_name(params),
    cross_breed_names: cross_breed_names(params),
    postal_code: postal_code(params),
    ...getSharedSteps(params, 'native'),
    pet_quote: nativeQuote(params),
    pet_health_v2_consent: pet_health_v2_consent(params),
    pet_health_v2_purchase: pet_health_v2_purchase(params),
  }) as const satisfies AnySteps

export const getWebSteps = (params: StepConfigParams) =>
  ({
    pet_details: pet_details(params),
    ...getSharedSteps(params, 'web'),
  }) as const satisfies AnySteps
